import { useCallback } from 'react';
import { getMovieService } from '../service/ServiceRegistry';
import type { FetchDataFn, TableQueryParams, TableDataResponse } from '../types/table';
import type {
  Movie,
  MoviesQueryParams,
  CreateMovieRequest,
  UpdateMovieRequest,
  MovieDetailsResponse,
  InitiateMultipartUploadRequest,
  InitiateMultipartUploadResponse,
  CompleteMultipartUploadRequest,
  VideoVariant,
  VideoMultipartStatusResponse,
  VideoMultipartRegenerateRequest,
  VideoMultipartPresignResponse,
  PreviewImageResponse,
  PreviewImageMetadataRequest,
  PreviewImageUpdateRequest,
} from '../types/api/movie';

/**
 * Custom hook for movie management
 * 
 * Integrates MovieService with the DataTable component
 * Handles data transformation between API and table formats
 */
export function useMovies() {
  const movieService = getMovieService();

  /**
   * Fetch function compatible with DataTable
   */
  const fetchMovies: FetchDataFn<Movie> = useCallback(
    async (params: TableQueryParams): Promise<TableDataResponse<Movie>> => {
      try {
        // API now expects 1-based page index; pass through directly
        const apiParams: MoviesQueryParams = {
          page: params.page,
          size: params.pageSize,
        };

        // Add search parameter if provided
        if (params.search) {
          apiParams.keyword = params.search;
        }

        // Extract accessLevel filter if present
        if (params.filters?.accessLevel?.value) {
          const accessLevelValue = params.filters.accessLevel.value;
          if (accessLevelValue && accessLevelValue !== '') {
            apiParams.accessLevel = accessLevelValue;
          }
        }

        // Extract contentRegion filter if present ("myanmar" | "international")
        if (params.filters?.contentRegion?.value) {
          const contentRegionValue = params.filters.contentRegion.value;
          if (contentRegionValue === 'myanmar' || contentRegionValue === 'international') {
            apiParams.contentRegion = contentRegionValue;
          }
        }

        // Fetch data from API
        const response = await movieService.getMovies(apiParams);
        
        return {
          data: response.items,
          pagination: {
            page: response.page,
            pageSize: response.size,
            total: response.totalItems,
            totalPages: response.totalPages,
            hasNext: response.hasNext,
            hasPrev: response.hasPrevious,
          },
        };
      } catch (error) {
        console.error('Failed to fetch movies:', error);
        throw new Error('Failed to load movies. Please try again.');
      }
    },
    [movieService]
  );

  return {
    fetchMovies,
  };
}

/**
 * Hook for movie actions
 * Provides functions to perform actions on movies
 */
export function useMovieActions() {
  const movieService = getMovieService();

  const createMovie = useCallback(
    async (movieData: CreateMovieRequest, thumbnailFile?: File): Promise<Movie> => {
      try {
        return await movieService.createMovie(movieData, thumbnailFile);
      } catch (error) {
        console.error('Failed to create movie:', error);
        throw error;
      }
    },
    [movieService]
  );

  const updateMovie = useCallback(
    async (movieId: string, updateData: UpdateMovieRequest): Promise<Movie> => {
      try {
        return await movieService.updateMovie(movieId, updateData);
      } catch (error) {
        console.error('Failed to update movie:', error);
        throw error;
      }
    },
    [movieService]
  );

  const updateMovieThumbnail = useCallback(
    async (movieId: string, thumbnailFile: File): Promise<Movie> => {
      try {
        return await movieService.updateThumbnail(movieId, thumbnailFile);
      } catch (error) {
        console.error('Failed to update movie thumbnail:', error);
        throw error;
      }
    },
    [movieService]
  );

  const deleteMovie = useCallback(
    async (movieId: string): Promise<void> => {
      try {
        await movieService.deleteMovie(movieId);
      } catch (error) {
        console.error('Failed to delete movie:', error);
        throw error;
      }
    },
    [movieService]
  );

  const getMovie = useCallback(
    async (movieId: string): Promise<MovieDetailsResponse> => {
      try {
        const response =  await movieService.getMovie(movieId);
        return response;
      } catch (error) {
        console.error('Failed to fetch movie:', error);
        throw error;
      }
    },
    [movieService]
  );

  const initiateVideoUpload = useCallback(
    async (
      movieId: string,
      payload: InitiateMultipartUploadRequest
    ): Promise<InitiateMultipartUploadResponse> => {
      try {
        return await movieService.initiateMultipartVideoUpload(movieId, payload);
      } catch (error) {
        console.error('Failed to initiate video upload:', error);
        throw error;
      }
    },
    [movieService]
  );

  const completeVideoUpload = useCallback(
    async (
      movieId: string,
      payload: CompleteMultipartUploadRequest
    ): Promise<VideoVariant> => {
      try {
        return await movieService.completeMultipartVideoUpload(movieId, payload);
      } catch (error) {
        console.error('Failed to complete video upload:', error);
        throw error;
      }
    },
    [movieService]
  );

  const abortVideoUpload = useCallback(
    async (params: { key: string; uploadId: string }): Promise<void> => {
      try {
        await movieService.abortMultipartVideoUpload(params);
      } catch (error) {
        console.error('Failed to abort video upload:', error);
        throw error;
      }
    },
    [movieService]
  );

  const getMultipartUploadStatus = useCallback(
    async (
      movieId: string,
      key: string,
      uploadId: string
    ): Promise<VideoMultipartStatusResponse> => {
      try {
        return await movieService.getMultipartUploadStatus(movieId, key, uploadId);
      } catch (error) {
        console.error('Failed to get multipart upload status:', error);
        throw error;
      }
    },
    [movieService]
  );

  const regenerateMultipartPartUrls = useCallback(
    async (
      movieId: string,
      request: VideoMultipartRegenerateRequest
    ): Promise<VideoMultipartPresignResponse> => {
      try {
        return await movieService.regenerateMultipartPartUrls(movieId, request);
      } catch (error) {
        console.error('Failed to regenerate multipart part URLs:', error);
        throw error;
      }
    },
    [movieService]
  );

  const deleteVideo = useCallback(
    async (contentId: string, fileId: string): Promise<void> => {
      try {
        await movieService.deleteVideo(contentId, fileId);
      } catch (error) {
        console.error('Failed to delete video:', error);
        throw error;
      }
    },
    [movieService]
  );

  const uploadPreviewImages = useCallback(
    async (
      movieId: string,
      files: File[],
      metadata?: PreviewImageMetadataRequest[]
    ): Promise<PreviewImageResponse[]> => {
      try {
        const batchMetadata = metadata ? { metadata } : undefined;
        return await movieService.uploadPreviewImages(movieId, files, batchMetadata);
      } catch (error) {
        console.error('Failed to upload movie preview images:', error);
        throw error;
      }
    },
    [movieService]
  );

  const updatePreviewImageMetadata = useCallback(
    async (
      movieId: string,
      previewImageId: string,
      payload: PreviewImageUpdateRequest
    ): Promise<PreviewImageResponse> => {
      try {
        return await movieService.updatePreviewImageMetadata(movieId, previewImageId, payload);
      } catch (error) {
        console.error('Failed to update movie preview image metadata:', error);
        throw error;
      }
    },
    [movieService]
  );

  const updatePreviewImageWithFile = useCallback(
    async (
      movieId: string,
      previewImageId: string,
      payload: PreviewImageUpdateRequest,
      file?: File
    ): Promise<PreviewImageResponse> => {
      try {
        return await movieService.updatePreviewImageWithFile(movieId, previewImageId, payload, file);
      } catch (error) {
        console.error('Failed to update movie preview image with file:', error);
        throw error;
      }
    },
    [movieService]
  );

  return {
    createMovie,
    updateMovie,
    updateMovieThumbnail,
    deleteMovie,
    getMovie,
    initiateVideoUpload,
    completeVideoUpload,
    abortVideoUpload,
    deleteVideo,
    getMultipartUploadStatus,
    regenerateMultipartPartUrls,
    uploadPreviewImages,
    updatePreviewImageMetadata,
    updatePreviewImageWithFile,
  };
}
